import Phaser from 'phaser';
import { Player } from '../objects/Player';
import { Projectile } from '../objects/Projectile';
import { setGameContext, getGameContext } from '../utils/context';
import type { GameContext } from '../types/GameContext';
// Audio keys centralized in utils mapping layer
import { BGM, SFX } from '../audio/AudioKeys';
// Tiled integration
import { MapEntityParser } from '../utils/MapEntityParser';
import { LAYERS } from '../utils/mapConstants';
// Spawner centralizes all spawning logic
import { Spawner } from '../utils/Spawner';
// Level data normalizer
import { normalizeLevelData } from '../utils/LevelDataNormalizer';
import { initSceneContext } from '../utils/initContext';
import type { SurvivalTime } from '../types/SurvivalTime';
import type { GameOverData } from '../types/GameOverData';

export class GameScene extends Phaser.Scene {
    private player!: Player;


    // Survival timer state
    private survival: SurvivalTime = { startedAt: 0, elapsedMs: 0, running: false };

    private map!: Phaser.Tilemaps.Tilemap;
    private tileset!: Phaser.Tilemaps.Tileset;
    private groundLayer!: Phaser.Tilemaps.TilemapLayer;
    private wallLayer!: Phaser.Tilemaps.TilemapLayer;

    // Task 5.5/5.6: Projectiles group for bullets
    private projectiles!: Phaser.Physics.Arcade.Group;

    // Added: keep zombies group reference and a game-over guard
    private zombies!: Phaser.Physics.Arcade.Group;
    private gameOver: boolean = false;

    // Task 5.1: pistol power-ups group
    private pistolPowerups!: Phaser.Physics.Arcade.Group;

    // New groups from parser-driven spawning
    private waterBottles!: Phaser.Physics.Arcade.Group; // pooled group (static-like bodies)
    private enemiesGroup!: Phaser.Physics.Arcade.Group;

    // Spawner instance
    private spawner!: Spawner;

    // Debug graphics for Arcade + Tilemap collisions
    private debugGraphics?: Phaser.GameObjects.Graphics;

    /**
     * Task 2.2: Scene-authoritative watermeter state and timer.
     * Single source of truth for water seconds remaining.
     */
    public currentWaterSeconds: number = 0; // Initialized in create()
    private waterTimer?: Phaser.Time.TimerEvent;

    constructor() {
        super({ key: 'GameScene' });
    }

    create() {
        // Ensure GameContext and SoundManager are initialized for this scene
        initSceneContext(this);

        // Initialize survival timer and ensure HUD is running with parentKey
        this.survival.startedAt = this.time.now;
        this.survival.elapsedMs = 0;
        this.survival.running = true;
        if (!this.scene.isActive('HUD')) {
            this.scene.launch('HUD', { parentKey: this.scene.key });
        }
        // Defer bringToTop until end of create() after all objects are created
        // (a second bringToTop is called at the end of create)

        // Robust (re)initialization for restarts: ensure world & input are enabled and state is reset.
        // Fix for "no movement after restart": physics may remain paused from last run; resume here.
        this.physics.world.resume();
        if (this.input && this.input.keyboard) {
            this.input.enabled = true;
            this.input.keyboard.enabled = true;
        }
        // Reset gameOver guard in case the scene instance is reused by Phaser
        this.gameOver = false;

        // Attempt to crossfade to gameplay BGM (guarded by SoundManager for missing assets)
        try {
            const ctx = getGameContext(this);
            ctx.sound?.playMusic(BGM.Level1, { loop: true, fadeInMs: 600, volume: 0.5 });
        } catch {
            // context may not be ready if BootScene didn't publish; safe to ignore
        }

        // Task 2.2: Initialize water state and timer on scene as the single source of truth.
        // TODO(config): make this configurable/dynamic (e.g., per difficulty)
        const WATER_START_SECONDS = 30;
        this.currentWaterSeconds = WATER_START_SECONDS;

        // Ensure previous timer (if any) is removed to avoid duplicates on restarts.
        if (this.waterTimer) {
            this.waterTimer.remove(false);
            this.waterTimer = undefined;
        }
        // Create Phaser looping timer that decrements once per second and clamps at 0.
        // Keep logic here, UI reads this.currentWaterSeconds.
        this.waterTimer = this.time.addEvent({
            delay: 1000, // ms
            loop: true,
            callback: () => {
                if (this.currentWaterSeconds > 0) {
                    this.currentWaterSeconds = Math.max(0, this.currentWaterSeconds - 1);
                }
                // No speed changes here (Task 2.4). HUD will pick up changes below.
            }
        });

        // Create the tilemap using keys preloaded in BootScene
        // BootScene loads: tilemapTiledJSON('level1') and image('tiles')
        this.map = this.make.tilemap({ key: 'level1' });

        // Tiled JSON references a tileset named "ground_tileset"; use loaded image key "tiles"
        const tileset = this.map.addTilesetImage('ground_tileset', 'tiles');
        if (!tileset) {
            throw new Error('Failed to load tileset');
        }
        this.tileset = tileset;

        // Ground/background layer (keep rendering as before)
        const groundLayer = this.map.createLayer(LAYERS.Ground, this.tileset, 0, 0);
        if (!groundLayer) {
            throw new Error('Failed to create ground layer');
        }
        this.groundLayer = groundLayer;

        // Walls layer from Tiled. Use canonical LAYERS.Walls to respect parser constants.
        const wallsLayer = this.map.createLayer(LAYERS.Walls, this.tileset, 0, 0);
        if (!wallsLayer) {
            throw new Error('Failed to create Walls layer');
        }
        this.wallLayer = wallsLayer as Phaser.Tilemaps.TilemapLayer;

        // Configure collisions for the wall layer.
        // Prefer Tiled per-tile property "collides: true" if present; fallback to non-empty tiles.
        // This keeps rendering unchanged and localizes physics to walls only.
        this.wallLayer.setCollisionByProperty({ collides: true });

        // Fallback when tileset doesn't carry a "collides" property:
        // We detect "no collisions configured" by checking a representative tile; if none found, apply exclusion rule.
        let hasAnyCollision = false;
        this.wallLayer.forEachTile((t) => {
            if ((t as any).collides === true) {
                hasAnyCollision = true;
            }
        }, undefined, 0, 0, this.map.width, this.map.height);
        if (!hasAnyCollision) {
            this.wallLayer.setCollisionByExclusion([-1], true);
        }

        this.wallLayer.setDepth(4);

        // -------------
        // Parse Tiled Entities and Paths object layers (Entities, Paths)
        // -------------
        // Entities: PlayerSpawn, Powerup(type=Pistol, ammo?, respawn?, rotate?), Consumable(type=WaterBottle, heal?, respawn?, rotate?), Enemy(...)
        // Paths: polyline with custom properties id (numeric), closed (bool). Enemy may reference PROPS.patrolPathId.
        const parse = MapEntityParser.parse(this.map);
        // Minimal startup logging
        console.info(
            `Parsed counts: spawn=${parse.entities.spawn ? 'ok' : 'missing'}, powerups=${parse.entities.powerups.length}, consumables=${parse.entities.consumables.length}, enemies=${parse.entities.enemies.length}`
        );
        parse.warnings.forEach(w => console.warn(w));

        // Normalize parsed entities/paths once, providing policy and map bounds
        const normalized = normalizeLevelData({
            entities: parse.entities,
            paths: parse.paths,
            policy: { pistolFallback: true }, // keep previous fallback behavior via policy
            map: { widthInPixels: this.map.widthInPixels, heightInPixels: this.map.heightInPixels },
        });
        if (normalized.warnings.length) {
            normalized.warnings.forEach(w => console.warn(w));
        }

        // ----------------
        // Spawn the player
        // ----------------
        const spawnX = parse.entities.spawn?.x ?? this.map.widthInPixels / 2;
        const spawnY = parse.entities.spawn?.y ?? this.map.heightInPixels / 2;
        this.player = new Player(this, spawnX, spawnY);
        this.player.setDepth(5);
        // Ensure active body
        this.player.setActive(true).setVisible(true);
        const pBody = this.player.body as Phaser.Physics.Arcade.Body | null;
        if (pBody) pBody.enable = true;

        // Collide player with walls
        this.physics.add.collider(this.player, this.wallLayer);

        // Spawner handles enemy group vs walls collisions internally after creation

        // ---------------------------
        // Camera and world bounds
        // ---------------------------
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.physics.world.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

        // ---------------------------
        // Task 5.5/5.6: Projectiles
        // ---------------------------
        this.projectiles = this.physics.add.group({
            allowGravity: false,
            classType: Projectile,
            runChildUpdate: true,
            immovable: false,
            dragX: 0,
            dragY: 0,
            bounceX: 0,
            bounceY: 0
        });

        // Destroy bullets when they hit walls
        this.physics.add.collider(
            this.projectiles,
            this.wallLayer,
            this.onProjectileHitsWall as unknown as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
            undefined,
            this
        );
        this.projectiles.on('add', (gameObject: Phaser.GameObjects.GameObject) => {
            const body = (gameObject as any).body as Phaser.Physics.Arcade.Body | undefined;
            if (body) {
                body.setAllowGravity(false);
                body.setImmovable(false);
                body.setDrag(0, 0);
                body.setDamping(false);
            }
        });

        // ---------------------------------
        // Groups for pickups and enemies via Spawner
        // ---------------------------------
        this.spawner = new Spawner(this, this.map, this.wallLayer);

        // Powerups (from normalized strict slice)
        this.spawner.spawnPowerupsFromConfig(normalized.entities.powerups);

        // Wire pistol pickup overlap
        this.spawner.wirePistolPickupOverlap(() => this.player as any);

        // Water bottles with scene-authoritative water seconds bridge (normalized strict slice)
        this.spawner.spawnWaterBottlesFromConfig(
            normalized.entities.consumables,
            () => this.player as any,
            () => this.currentWaterSeconds,
            (v: number) => { this.currentWaterSeconds = v; }
        );

        // Enemies (normalized strict slices for entities and paths)
        this.spawner.spawnEnemiesFromConfig(
            normalized.entities.enemies,
            normalized.paths
        );

        // Retrieve created groups for context publication and other use
        const groups = this.spawner.getGroups();
        this.pistolPowerups = groups.pistolPowerups;
        this.waterBottles = groups.waterBottles; // now a pooled Group
        this.enemiesGroup = groups.enemiesGroup;

        // Publish/merge the scene GameContext for typed, decoupled access from game objects.
        // IMPORTANT: Preserve existing fields (e.g., sound) initialized by initSceneContext(this).
        const existingCtx = getGameContext(this);
        const mergedCtx: GameContext = {
            ...existingCtx,
            projectiles: this.projectiles,
            zombies: this.enemiesGroup,
            pistolPowerups: this.pistolPowerups
        };
        setGameContext(this, mergedCtx);

        // Centralize all enemy-related overlaps (projectiles vs enemies and player vs enemies) in Spawner
        this.spawner.wireAllEnemyOverlaps(
            this.projectiles,
            () => this.player as any,
            this.onPlayerHitByZombie as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
            10000
        );

        // Player overlaps powerups and consumables are wired in their respective spawners

        // Debug helpers: render tile collision shapes and arcade bodies when enabled via query/hash
        const debugEnabled = this.isDebugEnabled();
        if (debugEnabled) {
            this.enableDebugDraw();
        }

        // HUD setup continues below...


        // Lifecycle: clean up timer on shutdown to avoid leaks/duplicates on restart.
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            if (this.waterTimer) {
                this.waterTimer.remove(false);
                this.waterTimer = undefined;
            }
        });
        // Optionally align with pause/resume
        this.events.on(Phaser.Scenes.Events.PAUSE, () => {
            if (this.waterTimer) {
                this.waterTimer.paused = true;
            }
        });
        this.events.on(Phaser.Scenes.Events.RESUME, () => {
            if (this.waterTimer) {
                this.waterTimer.paused = false;
            }
        });

        // Ensure HUD renders above everything created in this scene's create()
        // This runs last to avoid later-created objects appearing above HUD.
        if (this.scene.isActive('HUD')) {
            this.scene.bringToTop('HUD');
        }
    }

    // Handle player-zombie contacts and transition to GameOverScene safely
    // Signature widened to satisfy ArcadePhysicsCallback typing
    private onPlayerHitByZombie(
        obj1: Phaser.GameObjects.GameObject | Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile,
        obj2: Phaser.GameObjects.GameObject | Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | Phaser.Tilemaps.Tile
    ): void {
        if (this.gameOver) return;
        // Determine which is the player game object (in case order varies)
        const maybePlayer = (obj1 as any).body ? obj1 : obj2;
        const player = maybePlayer as Player;

        this.gameOver = true;

        // Stop further motion/inputs and overlaps
        if ((player as any).setVelocity) {
            (player as Player).setVelocity(0, 0);
        }
        if ((player as any).body) {
            ((player as any).body as Phaser.Physics.Arcade.Body).enable = false;
        }

        // Optionally pause physics world to avoid additional callbacks this tick
        // Note: We will pause only temporarily and rely on scene switch; GameScene.create() explicitly resumes.
        this.physics.world.pause();

        // Transition to GameOverScene; slight delay prevents same-tick re-entry
        this.time.delayedCall(50, () => {
            // Optional: play a game-over sting
            try {
                const ctx = getGameContext(this);
                ctx.sound?.playSfx(SFX.PlayerDeath, { volume: 0.7 });
            } catch {}
            // Survival timer finalization and HUD stop before GameOver
            this.survival.running = false;
            const finalMs = Math.round(this.survival.elapsedMs);
            if (this.scene.isActive('HUD')) {
                this.scene.stop('HUD');
            }
            this.scene.start('GameOverScene', { survivalMs: finalMs } as GameOverData);
        });
    }

    update(time: number, delta: number) {
        if (this.gameOver) return;
        this.player.update();

        // Tick survival timer
        if (this.survival.running) {
            this.survival.elapsedMs += delta;
        }


        // Update debug graphics each frame to reflect camera scroll
        if (this.debugGraphics) {
            this.drawDebug();
        }
        // Update all zombies
        // This is not strictly necessary for basic wandering, but good practice for future AI
        // Placeholders: future AI update hooks would use enemy.patrolData when present.
        // this.enemiesGroup.children.each(function(e) {
        //   const enemy = e as Phaser.Physics.Arcade.Sprite & { patrolData?: any };
        //   // TODO: implement patrolling and chasing behavior
        // }, this);
    }

    // --- Debug helpers ---

    // Toggle via URL contains ?debug or #debug
    private isDebugEnabled(): boolean {
        return true//typeof window !== 'undefined' && (window.location.search.includes('debug') || window.location.hash.includes('debug'));
    }

    private enableDebugDraw() {
        // Use world-space graphics so debug overlays follow camera movement
        this.debugGraphics = this.add.graphics().setScrollFactor(1);
        this.physics.world.createDebugGraphic();

        // Ensure the Arcade world debug graphic also scrolls with the world
        if (this.physics.world.debugGraphic && (this.physics.world.debugGraphic as any).setScrollFactor) {
            (this.physics.world.debugGraphic as any).setScrollFactor(1);
        }

        // Make bodies visible in Arcade debug layer
        this.physics.world.drawDebug = true;
        // Draw initial
        this.drawDebug();
    }

    private drawDebug() {
        // Clear previous
        this.debugGraphics!.clear();

        // Draw Arcade bodies
        // World debugGraphic is a separate Graphics auto-managed by Phaser
        if (this.physics.world.drawDebug && this.physics.world.debugGraphic) {
            this.physics.world.debugGraphic.clear();
            // Keep its scroll factor in sync in case Phaser recreated it
            if ((this.physics.world.debugGraphic as any).setScrollFactor) {
                (this.physics.world.debugGraphic as any).setScrollFactor(1);
            }
        }

        // Draw tile collisions for wall layer
        // Phaser provides a helper to render collision faces
        this.wallLayer.renderDebug(this.debugGraphics!, {
            tileColor: null, // Non-colliding tiles
            collidingTileColor: new Phaser.Display.Color(243, 134, 48, 180),
            faceColor: new Phaser.Display.Color(40, 39, 37, 255)
        });
    }

    // (Spawner now owns all spawn helpers; removed from GameScene)

    // Task 5.5: Projectile hits wall -> destroy bullet
    private onProjectileHitsWall = (bullet: Phaser.GameObjects.GameObject) => {
        if (!bullet || (bullet as any).destroyed) return;
        (bullet as Phaser.GameObjects.GameObject).destroy();
    };
    
    
    // Public getter for HUD to query survival ms
    public getSurvivalMs(): number {
        return Math.round(this.survival.elapsedMs);
    }

    // Public getters for HUD ammo display
    public getPlayerShots(): number {
        return this.player ? Math.max(0, (this.player as any).shotsRemaining | 0) : 0;
    }

    public getHasPistol(): boolean {
        return this.player ? !!(this.player as any).hasPistol : false;
    }
    
    
    // (Fallback pistol placement handled inside Spawner)
    
    // (Pistol pickup overlap wired by Spawner)
}
